import { frankencoinChainData, frankencoinConfigFor } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { frankencoinLenderKey } from '../../public-data/frankencoin/convertPublic'
import {
  FRANKENCOIN_CALLS_PER_POSITION,
  getCachedFrankencoinPositions,
} from './userCallBuild'

/** Per-position detail attached to the debt row (raw strings). */
export interface FrankencoinPositionInfo {
  /** The position contract — the sub-account id and every write op's target. */
  position: string
  /** The ORIGINAL this position was cloned from (its market). */
  original: string
  /** Owner-declared liquidation price (raw, 36-dec scaled). */
  declaredPrice: string
  /** Unix seconds; a position past this is subject to forced sale. */
  expiration: string
  /** Non-zero while a Dutch-auction challenge is running against it. */
  challengedAmount: string
  /** ppm withheld into the equity reserve — needed to quote a close, since
   *  repaying burns with reserve credit and costs LESS than `minted`. */
  reserveContributionPPM: string
}

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

/**
 * Returns a [converter, expectedCallCount] tuple for Frankencoin user data.
 *
 * Layout mirrors `buildFrankencoinUserCall`, sliced via the discovery cache
 * (Teller pattern). Emits one `UserData` per market key
 * `FRANKENCOIN_<chainId>_<ORIGINAL>`, with ONE SUB-ACCOUNT PER POSITION
 * CONTRACT (a user can hold many clones of the same original).
 *
 * `debt` is the FACE `minted` amount: it includes the reserve that will be
 * credited back on repay, so it OVERSTATES what the user must supply to
 * close. That is the conservative direction for a health figure, and
 * `reserveContributionPPM` rides along so a quote layer can compute the
 * true close cost.
 */
export const getFrankencoinUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const cfg = frankencoinConfigFor(lender, chainId)
  const markets = frankencoinChainData(lender, chainId)?.markets ?? []
  const discovery = getCachedFrankencoinPositions(chainId, account)
  const positions = discovery?.positions ?? []
  const expected =
    cfg && markets.length > 0 && positions.length > 0
      ? positions.length * FRANKENCOIN_CALLS_PER_POSITION
      : 0

  return [
    (data: any[]) => {
      if (!cfg || expected === 0) return undefined
      if (!data || data.length < expected) return undefined

      const debtAddr = cfg.zchf.toLowerCase()
      const debtDecimals = cfg.debtDecimals ?? 18
      const marketByOriginal = new Map(
        markets.map((m) => [m.position.toLowerCase(), m]),
      )
      const lowerAccount = account.toLowerCase()

      // Group per market key; sub-account per position contract.
      const perMarket = new Map<
        string,
        {
          market: (typeof markets)[number]
          rows: {
            info: FrankencoinPositionInfo
            coll: bigint
            debt: bigint
          }[]
        }
      >()

      positions.forEach((p, i) => {
        const base = i * FRANKENCOIN_CALLS_PER_POSITION
        const ownerRes = data[base]
        // A failed read is not an empty position — skip rather than
        // reporting the user as having closed it.
        if (isFailedCall(ownerRes)) return
        // Re-verify ownership on-chain: the API is a pointer, and a
        // position can change hands between the book fetch and now.
        if (String(ownerRes).toLowerCase() !== lowerAccount) return

        const minted = big(data[base + 1])
        const price = big(data[base + 2])
        const expiration = big(data[base + 3])
        const challenged = big(data[base + 4])
        const coll = big(data[base + 5])
        if (minted === 0n && coll === 0n) return

        const market = marketByOriginal.get(p.original)
        if (!market) return

        const key = frankencoinLenderKey(lender, chainId, market.position)
        if (!perMarket.has(key)) perMarket.set(key, { market, rows: [] })
        perMarket.get(key)!.rows.push({
          info: {
            position: p.position,
            original: p.original,
            declaredPrice: price.toString(),
            expiration: expiration.toString(),
            challengedAmount: challenged.toString(),
            reserveContributionPPM: String(market.reserveContribution),
          },
          coll,
          debt: minted,
        })
      })

      const out: { [lender: string]: UserData } = {}

      for (const [lenderKey, { market, rows }] of perMarket) {
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) continue

        const collAddr = market.collToken.toLowerCase()
        const collUid = createMarketUid(chainId, lenderKey, collAddr)
        const loanUid = createMarketUid(chainId, lenderKey, debtAddr)
        const collMeta = metaMap[collUid]
        const loanMeta = metaMap[loanUid]
        const collDecimals = collMeta?.asset?.decimals ?? market.collDecimals
        const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
        const collOracle = collMeta ? getOraclePrice(collMeta) : 0
        const collHist = collMeta?.price?.priceUsd24h ?? collDisplay
        const loanDisplay = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOracle = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanHist = loanMeta?.price?.priceUsd24h ?? loanDisplay

        const lendingPositions: Record<string, any> = {}
        const modes: Record<string, number> = {}
        const hist: Record<
          string,
          { totalDeposits24h: number; totalDebt24h: number }
        > = {}

        for (const row of rows) {
          const id = row.info.position
          const collStr = parseRawAmount(row.coll.toString(), collDecimals)
          const collNum = Number(collStr)
          const debtStr = parseRawAmount(row.debt.toString(), debtDecimals)
          const debtNum = Number(debtStr)

          lendingPositions[id] = {
            [collUid]: {
              marketUid: collUid,
              underlying: collAddr,
              deposits: collStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: collNum * collDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: collNum * collOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: true,
              claimableRewards: 0,
            },
            [loanUid]: {
              marketUid: loanUid,
              underlying: debtAddr,
              deposits: '0',
              debt: debtStr,
              debtStable: '0',
              depositsUSD: 0,
              debtUSD: debtNum * loanDisplay,
              debtStableUSD: 0,
              depositsUSDOracle: 0,
              debtUSDOracle: debtNum * loanOracle,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: false,
              claimableRewards: 0,
              frankencoinInfo: row.info,
            },
          }
          modes[id] = 0
          hist[id] = {
            totalDeposits24h: collNum * collHist,
            totalDebt24h: debtNum * loanHist,
          }
        }

        if (Object.keys(lendingPositions).length === 0) continue

        const userData = createMultiAccountTypeUserState(
          { chainId, account, lendingPositions, modes } as any,
          metaMap,
          hist,
        )
        if (userData && userData.data.length > 0) out[lenderKey] = userData
      }

      return Object.keys(out).length > 0 ? out : undefined
    },
    expected,
  ]
}
